import { matchIsValidTel } from 'mui-tel-input';
import { toast } from 'react-toastify';
import {
  CTAG_PATTERN,
  CAPITAL_ASSET_FIELDS,
  SAP_NUMBER_FIELD,
  SAP_NUMBER_PATTERN,
  SAP_NUMBER_FORMAT_MESSAGE,
} from './constants';
// import { ERRORS, COLUMNS } from "./constants";
import { isDateValid } from './date';
import { uploadError, bake } from './common';
import { Api } from './interface';

function IsNull(val) {
  return val === null || val.trim() === '' ? true : false;
}

function isInteger(val) {
  return !isNaN(val) && Number.isInteger(parseFloat(val));
}

/**
 * Checks if a value is an integer between a minimum and maximum value.
 *
 * @param {number} val value to check
 * @param {number} min minimum valid value for val
 * @param {number} max maximum valid value for val
 * @returns {boolean} true if val is an integer between min and max, false otherwise
 */
function isIntegerBetween(val, min, max) {
  return isInteger(val) && val >= min && val <= max;
}

function isFloat(val) {
  var expression = /^[+-]?\d+(\.\d+)?$/;
  var regex = new RegExp(expression);
  if (typeof val === 'number') {
    val = '' + val;
  }
  var result = val.match(regex);
  return result;
}

function isEmailFormat(val) {
  // eslint-disable-next-line no-useless-escape
  var expression =
    // eslint-disable-next-line no-useless-escape
    /^(([^<>()[\]\.,;:\s@\"]+(\.[^<>()[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i;
  var regex = new RegExp(expression);
  return val.match(regex);
}

function isURLFormat(val) {
  var expression =
    /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/gi;
  var regex = new RegExp(expression);
  return val.match(regex);
}
function isPDFFormat(val) {
  var expression = /\.(pdf)$/i;
  var regex = new RegExp(expression);
  return val.match(regex);
}

function isImageFormat(val) {
  var expression = /\.(gif|jpe?g|png|webp|bmp)$/i;
  var regex = new RegExp(expression);
  return val.match(regex);
}

function isValidBinary(val) {
  if (val === 0 || val === 1) {
    return true;
  } else {
    return false;
  }
}

// function will only not work for international phone numbers
function isPhoneNumberFormat(val) {
  return matchIsValidTel(val);
}

function displayErrorToast(item, errorContext) {
  toast.error(
    `Please ensure ${item.EntityMetaType} is a valid ${item.DataType} format. ${errorContext}`,
    {
      position: 'top-right',
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    }
  );
}

function validateField(item, value, options = {}) {
  const { mode = 'inline', rowIndex } = options;
  const isBulkValidation = mode === 'bulk';

  if (value === '') {
    return isBulkValidation ? [] : true;
  }

  const fail = (errorContext) => {
    if (isBulkValidation) {
      return [uploadError(rowIndex, value, item.EntityMetaType, errorContext)];
    }

    displayErrorToast(item, errorContext);
    return false;
  };

  if (
    item.EntityMetaType === CAPITAL_ASSET_FIELDS.C_TAG &&
    !CTAG_PATTERN.test(value)
  ) {
    return fail('e.g. C123456');
  }

  if (
    item.EntityMetaType === SAP_NUMBER_FIELD &&
    !SAP_NUMBER_PATTERN.test(value)
  ) {
    return fail(SAP_NUMBER_FORMAT_MESSAGE);
  }

  if (item.DataType === 'Date' && !isDateValid(value)) {
    console.log('invalid date');
    return fail('e.g. 12/30/1991 (mm/dd/yyyy)');
  }

  if (item.DataType === 'Binary' && !isValidBinary(value)) {
    return fail('e.g. 0 or 1');
  }

  if (item.DataType === 'Integer' && !isInteger(value)) {
    return fail('e.g. 1');
  }

  if (item.DataType === 'Float' && !isFloat(value)) {
    return fail('e.g. 1.0');
  }

  if (item.DataType === 'Email' && !isEmailFormat(value)) {
    return fail('e.g. sample@intel.com');
  }

  if (item.DataType === 'URL' && !isURLFormat(value)) {
    return fail('e.g. https://sample.com');
  }

  if (item.DataType === 'Image' && !isImageFormat(value)) {
    return fail('Accepted Formats: gif, jpeg, png, webp & bmp');
  }

  if (item.DataType === 'Phone Number' && !isPhoneNumberFormat(value)) {
    return fail('e.g. 012-345-6789');
  }

  if (item.DataType === '4-Digit Number' && !isIntegerBetween(value, 0, 9999)) {
    return fail('Must be a 4 digit number');
  }

  if (
    item.DataType === '12-Digit Number' &&
    !isIntegerBetween(value, 0, 999_999_999_999)
  ) {
    return fail('Must be a 12 digit number');
  }

  return isBulkValidation ? [] : true;
}

const IsRole = (list, userRoles) => {
  const roles = userRoles ?? bake({ key: 'roles' }) ?? [];

  // dev mode access for everything
  if (
    roles.some((r) => r.Role === 'Dev') &&
    bake({ key: 'devMode' }) === 'true'
  ) {
    return true;
  }

  return roles.some((r) => list.indexOf(r.Role) >= 0);
};

const IsDevMode = () => {
  return bake({ key: 'devMode' }) ?? 'false';
};

const IsDuplicate = (
  type,
  newItemName,
  localItemObjList,
  oldItemName = null,
  showMsg = true
) => {
  if (newItemName === '') {
    toast.error(`Unable to process an empty name.`, {
      position: toast.POSITION.TOP_RIGHT,
    });
    return true;
  }

  if (oldItemName) {
    if (newItemName === oldItemName) {
      return true;
    }
  }

  let found = [];
  newItemName = newItemName.toLowerCase();
  // maybe rework to pass in property to check instead of switching on type
  if (type == 'Entity') {
    found = localItemObjList.filter(
      (x) => x.EntityName.toLowerCase() === newItemName
    );
  } else if (type == 'Field') {
    found = localItemObjList.filter(
      (x) => x.EntityMetaType.toLowerCase() === newItemName
    );
  } else if (type == 'Group') {
    found = localItemObjList.filter(
      (x) => x.EntityMetaValue.toLowerCase() === newItemName
    );
  } else if (type == 'Template') {
    found = localItemObjList.filter(
      (x) => x.EntityType.toLowerCase() === newItemName
    );
  }

  if (oldItemName) {
    if (found.length >= 1 && newItemName === oldItemName.toLowerCase()) {
      return false;
    }
  }
  if (found.length > 0) {
    if (showMsg) {
      toast.error(
        `Unable to process '${newItemName}' as there is already an ${type} with that name`,
        {
          position: toast.POSITION.TOP_RIGHT,
        }
      );
    }
    return true;
  }
  return false;
};

const checkUnique = (item, val) => {
  Api({
    sp: 'checkUnique',
    json: {
      entityId: item.Entity_DBID,
      fieldId: item.EntityMetaType_DBID,
      val: val,
    },
  }).then((response) => {
    return response;
  });
};

export {
  isImageFormat,
  displayErrorToast,
  IsDuplicate,
  IsRole,
  IsNull,
  validateField,
  isInteger,
  isIntegerBetween,
  checkUnique,
  IsDevMode,
  isURLFormat,
  isPDFFormat,
};
